import React, { useState, useEffect, useRef } from 'react';
import { theme } from './theme';
import { getPlaceSuggestions } from './api/atlast';

// AirportAutocomplete — new (Aug 31, per Ali's report — Phase 4, item
// #2 from Ali's original 8-point list). Replaces PinTrip.js's three
// separate raw-text fields (destination, destination_code, origin_code)
// with a real autocomplete: type a city name OR an IATA code, pick the
// one right suggestion, and both the code and display name fill
// together as a single atomic selection — rather than three fields a
// person has to keep in sync by hand. See routes/places.py for why
// this is backed by Duffel's own Places API instead of a bundled
// static dataset.
//
// Debounced 300ms — short enough to feel responsive, long enough that
// fast typing doesn't fire a request per keystroke. Requires 2+
// characters before searching at all (matches the backend's own
// min_length=2), both to avoid a flood of near-useless single-letter
// queries and because Duffel's own suggestion quality is poor below
// that length anyway.
function AirportAutocomplete({ label, placeholder, value, onSelect }) {
  const [query, setQuery] = useState(value?.displayLabel || '');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    setQuery(value?.displayLabel || '');
  }, [value?.displayLabel]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const next = e.target.value;
    setQuery(next);
    setOpen(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (next.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await getPlaceSuggestions(next.trim());
        setSuggestions(res.suggestions || []);
      } catch {
        // Best-effort — a failed lookup just means an empty dropdown,
        // same as "no matches," rather than a hard error interrupting
        // pin creation over what's ultimately a convenience feature.
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleSelect = (place) => {
    const displayLabel = place.city_name && place.city_name !== place.name
      ? `${place.name} (${place.iata_code})`
      : `${place.name || place.city_name} (${place.iata_code})`;
    setQuery(displayLabel);
    setOpen(false);
    setSuggestions([]);
    onSelect({ iata_code: place.iata_code, displayLabel, iata_country_code: place.iata_country_code || null });
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {label && (
        <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: theme.structural, marginBottom: '4px' }}>
          {label}
        </label>
      )}
      <input
        type="text"
        value={query}
        onChange={handleChange}
        onFocus={() => setOpen(true)}
        placeholder={placeholder || 'City or airport code'}
        style={{
          width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: '14px',
          border: `2px solid ${theme.border}`, borderRadius: '0px', fontFamily: theme.fontDisplay
        }}
      />
      {open && (loading || suggestions.length > 0) && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
          background: theme.surface, border: `2px solid ${theme.border}`, borderTop: 'none',
          maxHeight: '220px', overflowY: 'auto'
        }}>
          {loading && (
            <div style={{ padding: '10px 12px', fontSize: '12.5px', color: theme.textMuted }}>Searching...</div>
          )}
          {!loading && suggestions.map((place, i) => (
            <div
              key={`${place.iata_code}-${i}`}
              onClick={() => handleSelect(place)}
              style={{
                padding: '10px 12px', fontSize: '13.5px', cursor: 'pointer',
                borderBottom: i < suggestions.length - 1 ? `1px solid ${theme.bg}` : 'none',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}
              onMouseDown={(e) => e.preventDefault()}
            >
              <span>
                <b>{place.name || place.city_name}</b>
                {place.city_name && place.city_name !== place.name && (
                  <span style={{ color: theme.textMuted }}> — {place.city_name}</span>
                )}
                {/* Country code (Sep 15, per Ali's report — real,
                    live-caught mix-up: "Pullman-Moscow Regional
                    Airport" reads like it could be Moscow, Russia at a
                    glance, when it's actually a small regional airport
                    in the US, near Moscow, Idaho. Nothing in this
                    dropdown distinguished the two before this — same
                    iata_country_code already added to the API response
                    for the currency-suggestion feature, reused here
                    for the same underlying problem: same-named places
                    in different countries. Kept as the bare 2-letter
                    code, not a full country name — Duffel's place data
                    doesn't carry one, and the code alone is enough to
                    catch a mismatch like this one. */}
                {place.iata_country_code && (
                  <span style={{ color: theme.textMuted, fontSize: '12.5px' }}> ({place.iata_country_code})</span>
                )}
              </span>
              <span style={{ fontFamily: theme.fontMono, fontWeight: 700, color: theme.structural, fontSize: '12px' }}>
                {place.iata_code}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AirportAutocomplete;